import { useEffect, useState, useCallback } from 'react';

interface ProgressData {
  type: string;
  documentId: string;
  message: string;
  progress: number;
  currentPage?: number;
  totalPages?: number;
  textLength?: number;
  pagesProcessed?: number;
}

interface DocumentProgress {
  [documentId: string]: ProgressData;
}

export const useDocumentProgress = (sessionKey: string) => {
  const [progress, setProgress] = useState<DocumentProgress>({});
  const [connected, setConnected] = useState(false);

  const connectSSE = useCallback(() => {
    if (!sessionKey) return;

    let reconnectAttempts = 0;
    const maxReconnectAttempts = 5;
    const reconnectDelay = 1000; // Base delay: 1 second
    let eventSource: EventSource | null = null;
    let connectionTimeout: NodeJS.Timeout | null = null;
    let lastHeartbeatTime = Date.now();

    // Connection monitoring - if no heartbeat for 40s, reconnect
    const monitorConnection = () => {
      connectionTimeout = setTimeout(() => {
        const timeSinceLastHeartbeat = Date.now() - lastHeartbeatTime;
        if (timeSinceLastHeartbeat > 40000 && eventSource?.readyState === EventSource.OPEN) {
          console.log('🔔 No heartbeat detected, reconnecting SSE...');
          if (eventSource) {
            eventSource.close();
          }
          connect();
        }
      }, 45000); // Check every 45 seconds
    };

    const connect = () => {
      try {
        // Clean up existing connection and timers
        if (eventSource) {
          eventSource.close();
        }
        if (connectionTimeout) {
          clearTimeout(connectionTimeout);
        }

        console.log(`🔄 Connecting SSE for session: ${sessionKey}`);
        eventSource = new EventSource(`/api/documents/progress/${sessionKey}`);

        eventSource.onopen = () => {
          console.log('✅ Progress SSE connected successfully');
          setConnected(true);
          reconnectAttempts = 0; // Reset on successful connection
          lastHeartbeatTime = Date.now(); // Reset heartbeat timer
          monitorConnection(); // Start connection monitoring
        };

        eventSource.onmessage = (event) => {
          try {
            const data: ProgressData = JSON.parse(event.data);

            // Update heartbeat timestamp for all messages (including heartbeats)
            lastHeartbeatTime = Date.now();

            if (data.type === 'connected') {
              console.log('📡 Progress tracking connected');
              return;
            }

            console.log('📊 Progress update received:', data);

            // Update progress for specific document
            setProgress(prev => ({
              ...prev,
              [data.documentId]: data
            }));

            // Remove completed documents after delay
            if (data.type === 'ocr_completed' || data.type === 'document_completed') {
              setTimeout(() => {
                setProgress(prev => {
                  const updated = { ...prev };
                  delete updated[data.documentId];
                  return updated;
                });
              }, 5000); // Keep completed status for 5 seconds
            }
          } catch (error) {
            console.error('❌ Failed to parse progress data:', error);
          }
        };

        eventSource.onerror = (error) => {
          console.error('❌ Progress SSE error:', error);
          setConnected(false);

          // Clear monitoring timeout
          if (connectionTimeout) {
            clearTimeout(connectionTimeout);
          }

          // Close current connection
          if (eventSource) {
            eventSource.close();
          }

          // Auto-reconnect with exponential backoff
          if (reconnectAttempts < maxReconnectAttempts) {
            reconnectAttempts++;
            const delay = reconnectDelay * Math.pow(2, reconnectAttempts - 1);
            console.log(`🔄 Reconnecting SSE... Attempt ${reconnectAttempts}/${maxReconnectAttempts} in ${delay}ms`);

            setTimeout(() => {
              connect();
            }, delay);
          } else {
            console.error('❌ SSE: Max reconnection attempts reached');
          }
        };

      } catch (error) {
        console.error('❌ SSE connection failed:', error);
        setConnected(false);
      }
    };

    // Initial connection
    connect();

    return () => {
      if (eventSource) {
        eventSource.close();
        eventSource = null;
      }
      if (connectionTimeout) {
        clearTimeout(connectionTimeout);
      }
      setConnected(false);
    };
  }, [sessionKey]);

  useEffect(() => {
    const cleanup = connectSSE();
    return cleanup;
  }, [connectSSE]);

  const getDocumentProgress = (documentId: string): ProgressData | null => {
    return progress[documentId] || null;
  };

  const isProcessing = (documentId: string): boolean => {
    const docProgress = progress[documentId];
    return docProgress && docProgress.progress < 100 &&
           docProgress.type !== 'document_completed' &&
           docProgress.type !== 'ocr_completed';
  };

  const clearProgress = (documentId: string) => {
    setProgress(prev => {
      const updated = { ...prev };
      delete updated[documentId];
      return updated;
    });
  };

  return {
    progress,
    connected,
    getDocumentProgress,
    isProcessing,
    clearProgress
  };
};